import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Grid, Typography, Button, LinearProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import { newFleetForm, newFleetFormOnSubmit } from "../../validation/schemas";
import { FleetContext } from "../../context";
import { SectionGridLayout, SectionGridContainer } from "../../components/Layout";
import { FilledTextField } from "../../components/FormElements";

const NewFleet = () => {
  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();
  const { createNewFleet } = useContext(FleetContext);
  const emptyForm = {
    country: "",
    state: "",
    city: "",
    abbreviation: "",
  };
  const [formData, setFormData] = useState(emptyForm);
  const [invalids, setInvalids] = useState([]);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(null);

  const handleFormChange = (e, field) => {
    const newFormData = newFleetForm.cast({ ...formData, [field]: e.target.value });
    setFormData(newFormData);
  };

  const handleSubmit = async () => {
    const formDataToSubmit = newFleetFormOnSubmit.cast(formData);
    setLoading(true);
    try {
      const newFleet = await createNewFleet(formDataToSubmit);
      history.push(`/fleets/${newFleet}`);
    } catch (err) {
      console.error(err.message);
      setServerError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    const validate = async () => {
      try {
        await newFleetForm.validate(formData, { abortEarly: false, recursive: true, strict: true });
        setInvalids([]);
      } catch (err) {
        setInvalids(err.errors);
      }
    };
    validate();
  }, [formData]);

  return (
    <Grid container justify="center">
      <SectionGridLayout gridProps={{ md: 4 }}>
        <SectionGridContainer spaceContents>
          <Grid item xs={12}>
            <Typography variant="h5">{t("Fleet.NewFleet.CreateNewFleet")}</Typography>
          </Grid>
          {serverError && (
            <Grid item xs={12}>
              <Typography color="secondary">{serverError}</Typography>
            </Grid>
          )}
          {Object.keys(emptyForm).map((field) => (
            <Grid item xs={12} key={field}>
              <FilledTextField
                value={formData[field]}
                label={t(`Fleet.NewFleet.${field}`)}
                onChange={(e) => handleFormChange(e, field)}
                underlineColor={theme.palette.text.secondary}
                error={dirtyFields.has(field) && invalids.some((err) => err.includes(field))}
                onBlur={() => setDirtyFields(new Set([...dirtyFields, field]))}
                helperText={dirtyFields.has(field) && invalids.find((err) => err.includes(field))}
              />
            </Grid>
          ))}
          <Grid item container xs={12} justify="flex-end">
            <Button onClick={() => history.push("/fleets")}>{t("Fleet.NewFleet.Cancel")}</Button>
            <Button
              onClick={handleSubmit}
              disabled={!!invalids.length}
              style={{ marginLeft: theme.spacing(2) }}
            >
              {t("Fleet.NewFleet.Submit")}
            </Button>
          </Grid>
          {loading && (
            <Grid item xs={12} style={{ padding: 0 }}>
              <LinearProgress color="secondary" data-testid="progress-bar" />
            </Grid>
          )}
        </SectionGridContainer>
      </SectionGridLayout>
    </Grid>
  );
};

export default NewFleet;
